import React from 'react';

// 3D-styled Mednosys logo
// Layered SVG with faux-3D bevel: outer shadow, extruded side face,
// glossy top face with radial highlight, animated pulse spike, glowing dot.
export default function Logo({ size = 44, showWordmark = true, tone = 'dark' }) {
  const textColor = tone === 'light' ? '#FFFFFF' : '#0F1B2D';
  return (
    <a href="/" className="flex items-center gap-3 group select-none" aria-label="Mednosys home">
      <div
        className="relative transition-transform duration-500 ease-out group-hover:-translate-y-0.5 group-hover:rotate-[4deg]"
        style={{ width: size + 8, height: size + 8, perspective: 400 }}
      >
        <svg
          width={size + 8}
          height={size + 8}
          viewBox="0 0 56 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="drop-shadow-[0_10px_18px_rgba(232,90,24,0.35)]"
        >
          <defs>
            {/* Top face gradient (glossy) */}
            <linearGradient id="topFace" x1="6" y1="6" x2="50" y2="50" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#FFC79A" />
              <stop offset="45%" stopColor="#FF8A3A" />
              <stop offset="100%" stopColor="#D9481A" />
            </linearGradient>
            {/* Side / extrusion */}
            <linearGradient id="sideFace" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B23D12" />
              <stop offset="100%" stopColor="#6B2308" />
            </linearGradient>
            {/* Top shine */}
            <radialGradient id="shine" cx="32%" cy="22%" r="55%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.85" />
              <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </radialGradient>
            {/* Rim highlight */}
            <linearGradient id="rim" x1="0" y1="0" x2="56" y2="56">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
              <stop offset="70%" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
            {/* Glow behind */}
            <radialGradient id="glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FF7A2E" stopOpacity="0.55" />
              <stop offset="70%" stopColor="#FF7A2E" stopOpacity="0" />
            </radialGradient>
            <filter id="softShadow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="1.4" />
              <feOffset dx="0" dy="1.5" result="o" />
              <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* ambient glow */}
          <circle cx="28" cy="32" r="22" fill="url(#glow)" />

          {/* extruded side face (offset behind) */}
          <rect x="5" y="9" width="46" height="46" rx="13" fill="url(#sideFace)" />

          {/* top glossy face */}
          <rect x="5" y="5" width="46" height="46" rx="13" fill="url(#topFace)" />
          {/* rim highlight */}
          <rect x="5.5" y="5.5" width="45" height="45" rx="12.5" fill="none" stroke="url(#rim)" strokeWidth="1.1" />
          {/* glossy highlight */}
          <rect x="5" y="5" width="46" height="46" rx="13" fill="url(#shine)" />

          {/* Heartbeat M mark */}
          <g filter="url(#softShadow)">
            <path
              d="M13 37 L13 19 L21 30 L25 25 L28 34 L31 22 L35 30 L43 19 L43 37"
              stroke="#FFFFFF"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>

          {/* animated peak dot with glow */}
          <circle cx="28" cy="34" r="3" fill="#FFFFFF" opacity="0.35">
            <animate attributeName="r" values="3;6;3" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.35;0;0.35" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <circle cx="28" cy="34" r="2" fill="#FFFFFF">
            <animate attributeName="r" values="2;2.8;2" dur="1.6s" repeatCount="indefinite" />
          </circle>
        </svg>
      </div>

      {showWordmark && (
        <div className="flex flex-col leading-none">
          <span
            className="font-semibold tracking-tight text-[20px]"
            style={{
              color: textColor,
              fontFamily: "'Space Grotesk', sans-serif",
              textShadow: tone === 'light' ? '0 1px 0 rgba(255,255,255,0.1)' : '0 1px 0 rgba(255,255,255,0.6)',
            }}
          >
            Mednosys
          </span>
          <span
            className="text-[10px] uppercase tracking-[0.24em] mt-0.5 font-semibold"
            style={{ color: tone === 'light' ? 'rgba(255,255,255,0.75)' : '#E85A18' }}
          >
            Enterprises
          </span>
        </div>
      )}
    </a>
  );
}
